import React, { useState } from 'react';
import OverviewTab from './OverviewTab';
import PropertyTab from './PropertyTab';
import JobsTab from './JobsTab';
import QuotesTab from './QuotesTab';
import NotesTab from './NotesTab';
// Files tab deferred for v1 launch — re-enable after Firebase Storage + virus scan land.
// import FilesTab from './FilesTab';
import PaymentHistoryTab from './PaymentHistoryTab';
import ActivityTimelineTab from './ActivityTimelineTab';
import LifecycleTab from './LifecycleTab';

/**
 * ProfileTabs - Tab navigation for client profile
 *
 * Tab state is internal by default, but can be controlled by a parent
 * (e.g. so the FinancialSummaryCard "View open invoices" action can jump to
 * the Payments tab). Pass `activeTab` + `onTabChange` to control it.
 */
const ProfileTabs = ({ client, activeTab: controlledTab, onTabChange }) => {
  const [internalTab, setInternalTab] = useState('overview');
  const activeTab = controlledTab ?? internalTab;
  const setActiveTab = onTabChange ?? setInternalTab;

  const tabs = [
    { id: 'overview',  label: 'Overview'  },
    { id: 'property',  label: 'Property'  },
    { id: 'activity',  label: 'Activity'  },
    { id: 'jobs',      label: 'Jobs'      },
    { id: 'quotes',    label: 'Quotes'    },
    { id: 'payments',  label: 'Payments'  },
    { id: 'notes',     label: 'Notes'     },
    // Files tab deferred for v1 launch.
    // { id: 'files',     label: 'Files'     },
    { id: 'lifecycle', label: 'Lifecycle' },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':  return <OverviewTab client={client} />;
      case 'property':  return <PropertyTab client={client} />;
      case 'activity':  return <ActivityTimelineTab client={client} />;
      case 'jobs':      return <JobsTab client={client} />;
      case 'quotes':    return <QuotesTab client={client} />;
      case 'payments':  return <PaymentHistoryTab client={client} />;
      case 'notes':     return <NotesTab client={client} />;
      // case 'files':     return <FilesTab client={client} />;
      case 'lifecycle': return <LifecycleTab client={client} />;
      default:          return <OverviewTab client={client} />;
    }
  };

  return (
    <div className="space-y-4">
      {/* Tab Navigation */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          {/*
            Sprint 288 — mobile-responsive tab strip.
            Horizontally scrollable on narrow viewports so every tab
            remains reachable in portrait orientation. Tabs stay on ONE
            line (`whitespace-nowrap` + `flex` instead of `flex-wrap`);
            desktop rendering is visually unchanged because the strip
            never actually needs to scroll at ≥sm widths.
          */}
          <nav
            className="flex gap-4 px-4 sm:px-6 overflow-x-auto whitespace-nowrap [-webkit-overflow-scrolling:touch] scrollbar-none"
            aria-label="Client profile tabs"
          >
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`profile-tab-${tab.id}`}
                className={`
                  shrink-0 py-4 px-1 border-b-2 font-medium text-sm transition-colors
                  ${
                    activeTab === tab.id
                      ? 'border-orange-500 text-orange-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-4 sm:p-6">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

export default ProfileTabs;
